import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Skeleton } from "@mui/material";
import { doc, getDoc } from "firebase/firestore";
import React, { useEffect, useRef, useState } from "react";
import { db } from "../../firebase";

const PRIMARY = "#000c66";
const GOLD = "#ffc000";
const CREAM = "#faf6ee";
const LIGHT = "#eef0fb";

const BEATITUDES = [
  {
    ref: "Matthew 5:3",
    text: "Blessed are the poor in spirit, for theirs is the kingdom of heaven.",
    group: "The Beatitudes",
  },
  {
    ref: "Matthew 5:4",
    text: "Blessed are those who mourn, for they will be comforted.",
    group: "The Beatitudes",
  },
  {
    ref: "Matthew 5:5",
    text: "Blessed are the meek, for they will inherit the earth.",
    group: "The Beatitudes",
  },
  {
    ref: "Matthew 5:6",
    text: "Blessed are those who hunger and thirst for righteousness, for they will be filled.",
    group: "The Beatitudes",
  },
  {
    ref: "Matthew 5:7",
    text: "Blessed are the merciful, for they will be shown mercy.",
    group: "The Beatitudes",
  },
  {
    ref: "Matthew 5:8",
    text: "Blessed are the pure in heart, for they will see God.",
    group: "The Beatitudes",
  },
  {
    ref: "Matthew 5:9",
    text: "Blessed are the peacemakers, for they will be called children of God.",
    group: "The Beatitudes",
  },
  {
    ref: "Matthew 5:10",
    text: "Blessed are those who are persecuted because of righteousness, for theirs is the kingdom of heaven.",
    group: "The Beatitudes",
  },
  {
    ref: "Matthew 5:11",
    text: "Blessed are you when people insult you, persecute you and falsely say all kinds of evil against you because of me.",
    group: "The Beatitudes",
  },
  {
    ref: "Matthew 5:12",
    text: "Rejoice and be glad, because great is your reward in heaven, for in the same way they persecuted the prophets before you.",
    group: "The Beatitudes",
  },
];

const VESSELS = [
  {
    ref: "Romans 9:21",
    text: "Does not the potter have the right to make out of the same lump of clay some pottery for special purposes and some for common use?",
    group: "Vessels of Honour",
  },
  {
    ref: "Romans 9:23",
    text: "What if he did this to make the riches of his glory known to the objects of his mercy, whom he prepared in advance for glory?",
    group: "Vessels of Honour",
  },
  {
    ref: "2 Timothy 2:21",
    text: "Those who cleanse themselves will be instruments for special purposes, made holy, useful to the Master and prepared to do any good work.",
    group: "Vessels of Honour",
  },
  {
    ref: "Ephesians 2:10",
    text: "For we are God's handiwork, created in Christ Jesus to do good works, which God prepared in advance for us to do.",
    group: "Vessels of Honour",
  },
  {
    ref: "1 Corinthians 3:9",
    text: "For we are co-workers in God's service; you are God's field, God's building.",
    group: "Vessels of Honour",
  },
  {
    ref: "Jeremiah 18:4",
    text: "But the pot he was shaping from the clay was marred in his hands; so the potter formed it into another pot, shaping it as seemed best.",
    group: "Vessels of Honour",
  },
];

const SCRIPTURES = [...BEATITUDES, ...VESSELS];

const STYLES = `
  @keyframes verseIn {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .votw-nav-btn:hover { background: ${PRIMARY} !important; color: #fff !important; }
  @media (max-width: 600px) {
    .votw-cols { grid-template-columns: 1fr !important; gap: 20px 0 !important; }
    .votw-hero { padding: 32px 20px 28px !important; }
    .votw-band { padding: 32px 20px 28px !important; }
  }
`;

const formatDob = (dob) => {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return dob;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const getInitials = (name) =>
  name
    ? name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "?";

const Diamond = () => (
  <span
    style={{
      display: "inline-block",
      width: 7,
      height: 7,
      background: GOLD,
      transform: "rotate(45deg)",
      flexShrink: 0,
    }}
  />
);

export const VesselOfTheWeek = () => {
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const isPausedRef = useRef(false);
  const timerRef = useRef(null);

  /* ── Firebase fetch (same as FaceOfTheWeek) ── */
  useEffect(() => {
    const getMember = async () => {
      try {
        const settingsSnap = await getDoc(doc(db, "settings", "faceOfWeek"));
        if (!settingsSnap.exists()) return;
        const { memberId } = settingsSnap.data();
        if (!memberId) return;
        const memberSnap = await getDoc(doc(db, "members", memberId));
        if (memberSnap.exists())
          setMember({ _id: memberSnap.id, ...memberSnap.data() });
      } catch (err) {
        console.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    getMember();
  }, []);

  /* ── Scripture autoplay ── */
  const startTimer = () => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (!isPausedRef.current)
        setActiveIdx((p) => (p + 1) % SCRIPTURES.length);
    }, 6000);
  };

  useEffect(() => {
    startTimer();
    return () => clearInterval(timerRef.current);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const go = (i) => {
    setActiveIdx(i);
    startTimer();
  };
  const prev = () =>
    go((activeIdx - 1 + SCRIPTURES.length) % SCRIPTURES.length);
  const next = () => go((activeIdx + 1) % SCRIPTURES.length);

  /* ── Derived ── */
  const departments = member?.department
    ? Array.isArray(member.department)
      ? member.department
      : member.department
          .split(",")
          .map((d) => d.trim())
          .filter(Boolean)
    : [];

  const current = SCRIPTURES[activeIdx];
  const pad = (n) => String(n).padStart(2, "0");

  return (
    <>
      <style>{STYLES}</style>

      <section style={{ background: "#f5f3ef", padding: "48px 16px" }}>
        <div
          style={{
            maxWidth: 820,
            margin: "0 auto",
            borderRadius: 10,
            overflow: "hidden",
            boxShadow:
              "0 8px 48px rgba(0,12,102,0.14), 0 2px 10px rgba(0,12,102,0.07)",
          }}
        >
          {/* ═══════════════ NAVY HERO ═══════════════ */}
          <div
            className="votw-hero"
            style={{
              background: "linear-gradient(168deg, #0a1f8f 0%, #000c66 78%)",
              padding: "46px 40px 40px",
              color: LIGHT,
            }}
          >
            {/* Top row: badge + week */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 28,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <span
                style={{
                  background: GOLD,
                  color: PRIMARY,
                  fontFamily: "Abel, sans-serif",
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.24em",
                  textTransform: "uppercase",
                  borderRadius: 999,
                  padding: "5px 16px",
                }}
              >
                Vessel of the Week
              </span>
              {member?.week && (
                <span
                  style={{
                    fontFamily: "Abel, sans-serif",
                    fontSize: 12,
                    color: "rgba(238,240,251,0.5)",
                    letterSpacing: "0.05em",
                  }}
                >
                  {member.week}
                </span>
              )}
            </div>

            {/* Avatar */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginBottom: 18,
              }}
            >
              {loading ? (
                <Skeleton
                  variant="circular"
                  width={160}
                  height={160}
                  sx={{ bgcolor: "rgba(255,255,255,0.08)" }}
                />
              ) : (
                <div
                  style={{
                    width: 160,
                    height: 160,
                    borderRadius: "50%",
                    overflow: "hidden",
                    flexShrink: 0,
                    boxShadow: `0 0 0 3px ${PRIMARY}, 0 0 0 7px ${GOLD}`,
                    background: `${GOLD}22`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {member?.imgSrc ? (
                    <img
                      src={member.imgSrc}
                      alt={member.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "top",
                      }}
                      draggable={false}
                    />
                  ) : (
                    <span
                      style={{
                        fontFamily: "Aref Ruqaa, serif",
                        fontWeight: 700,
                        fontSize: 48,
                        color: GOLD,
                      }}
                    >
                      {getInitials(member?.name || "")}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Name + meta */}
            {loading ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Skeleton
                  variant="text"
                  width={240}
                  height={52}
                  sx={{ bgcolor: "rgba(255,255,255,0.08)" }}
                />
                <Skeleton
                  variant="text"
                  width={160}
                  height={22}
                  sx={{ bgcolor: "rgba(255,255,255,0.08)" }}
                />
              </div>
            ) : !member ? (
              <p
                style={{
                  fontFamily: "Aref Ruqaa, serif",
                  fontSize: 18,
                  color: "rgba(238,240,251,0.35)",
                  textAlign: "center",
                  margin: 0,
                }}
              >
                Coming soon — check back next week.
              </p>
            ) : (
              <>
                <h2
                  style={{
                    fontFamily: "Sorts Mill Goudy, serif",
                    // fontWeight: 700,
                    fontSize: "clamp(1.8rem, 4.5vw, 2.6rem)",
                    color: "#fff",
                    margin: "0 0 6px",
                    textAlign: "center",
                    lineHeight: 1.1,
                  }}
                >
                  {member.name}
                </h2>
                <p
                  style={{
                    fontFamily: "Abel, sans-serif",
                    fontSize: 13,
                    color: "rgba(238,240,251,0.5)",
                    textAlign: "center",
                    margin: "0 0 18px",
                    letterSpacing: "0.06em",
                  }}
                >
                  {[member.gender, formatDob(member.dob)]
                    .filter(Boolean)
                    .join(" · ")}
                </p>

                {/* Department chips */}
                {departments.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      flexWrap: "wrap",
                      gap: 7,
                      marginBottom: 22,
                    }}
                  >
                    {departments.map((d) => (
                      <span
                        key={d}
                        style={{
                          fontFamily: "Abel, sans-serif",
                          fontSize: 11,
                          textTransform: "uppercase",
                          letterSpacing: "0.1em",
                          color: GOLD,
                          border: `1px solid ${GOLD}77`,
                          background: `${GOLD}1a`,
                          borderRadius: 999,
                          padding: "3px 12px",
                        }}
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                )}

                {/* Hairline divider */}
                <div
                  style={{
                    height: 1,
                    background: "rgba(238,240,251,0.1)",
                    margin: "0 0 22px",
                  }}
                />

                {/* 2-col: Verse + Hymn */}
                <div
                  className="votw-cols"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1.5fr 1fr",
                    gap: "0 28px",
                  }}
                >
                  <div>
                    <p
                      style={{
                        fontFamily: "Abel, sans-serif",
                        fontSize: 10,
                        textTransform: "uppercase",
                        letterSpacing: "0.22em",
                        color: GOLD,
                        margin: "0 0 8px",
                      }}
                    >
                      Favourite Verse
                    </p>
                    {member.favBibleVerse ? (
                      <p
                        style={{
                          fontFamily: "Aref Ruqaa, serif",
                          fontStyle: "italic",
                          fontSize: "clamp(0.82rem, 1.6vw, 0.95rem)",
                          color: LIGHT,
                          lineHeight: 1.65,
                          margin: 0,
                        }}
                      >
                        "{member.favBibleVerse}"
                      </p>
                    ) : (
                      <p
                        style={{
                          fontFamily: "Abel, sans-serif",
                          fontSize: 13,
                          color: "rgba(238,240,251,0.3)",
                          fontStyle: "italic",
                          margin: 0,
                        }}
                      >
                        —
                      </p>
                    )}
                  </div>

                  <div>
                    <p
                      style={{
                        fontFamily: "Abel, sans-serif",
                        fontSize: 10,
                        textTransform: "uppercase",
                        letterSpacing: "0.22em",
                        color: GOLD,
                        margin: "0 0 8px",
                      }}
                    >
                      Favourite Hymn
                    </p>
                    {member.favHymns ? (
                      <>
                        <p
                          style={{
                            fontFamily: "Aref Ruqaa, serif",
                            fontWeight: 700,
                            fontSize: "clamp(0.88rem, 1.8vw, 1rem)",
                            color: "#fff",
                            margin: "0 0 3px",
                          }}
                        >
                          {member.favHymns}
                        </p>
                        <p
                          style={{
                            fontFamily: "Abel, sans-serif",
                            fontSize: 11,
                            color: "rgba(238,240,251,0.38)",
                            margin: 0,
                            letterSpacing: "0.05em",
                          }}
                        >
                          CCC Hymnal
                        </p>
                      </>
                    ) : (
                      <p
                        style={{
                          fontFamily: "Abel, sans-serif",
                          fontSize: 13,
                          color: "rgba(238,240,251,0.3)",
                          fontStyle: "italic",
                          margin: 0,
                        }}
                      >
                        —
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* ═══════════════ CREAM SCRIPTURE BAND ═══════════════ */}
          <div
            className="votw-band"
            style={{ background: CREAM, padding: "38px 40px 32px" }}
            onMouseEnter={() => {
              isPausedRef.current = true;
            }}
            onMouseLeave={() => {
              isPausedRef.current = false;
            }}
          >
            {/* Eyebrow */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                marginBottom: 28,
              }}
            >
              <Diamond />
              <p
                style={{
                  fontFamily: "Abel, sans-serif",
                  fontSize: 10,
                  textTransform: "uppercase",
                  letterSpacing: "0.24em",
                  color: GOLD,
                  margin: 0,
                }}
              >
                {/* This Week's Scriptures */}
                {current.group}
              </p>
              <Diamond />
            </div>

            {/* Verse */}
            <div
              style={{
                maxWidth: 620,
                margin: "0 auto",
                textAlign: "center",
                minHeight: 130,
              }}
            >
              <div
                key={activeIdx}
                style={{ animation: "verseIn 0.6s ease-out both" }}
              >
                {/* <p
                  style={{
                    fontFamily: "Abel, sans-serif",
                    fontSize: 10,
                    textTransform: "uppercase",
                    letterSpacing: "0.28em",
                    color: GOLD,
                    margin: "0 0 10px",
                  }}
                >
                  {current.group}
                </p> */}
                <p
                  style={{
                    fontFamily: "Aref Ruqaa, serif",
                    fontStyle: "italic",
                    fontSize: "clamp(1.05rem, 2.2vw, 1.45rem)",
                    color: "#2a2f38",
                    lineHeight: 1.6,
                    margin: "0 0 12px",
                  }}
                >
                  "{current.text}"
                </p>
                <p
                  style={{
                    fontFamily: "Abel, sans-serif",
                    fontSize: 11,
                    textTransform: "uppercase",
                    letterSpacing: "0.2em",
                    color: PRIMARY,
                    margin: 0,
                  }}
                >
                  {current.ref}
                </p>
              </div>
            </div>

            {/* Controls */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 14,
                marginTop: 24,
              }}
            >
              {/* Progress ticks */}
              <div
                style={{
                  display: "flex",
                  gap: 4,
                  flexWrap: "wrap",
                  justifyContent: "center",
                }}
              >
                {SCRIPTURES.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => go(i)}
                    aria-label={`Scripture ${i + 1}`}
                    style={{
                      width: activeIdx === i ? 20 : 6,
                      height: 6,
                      borderRadius: 3,
                      background:
                        activeIdx === i ? GOLD : "rgba(0,12,102,0.18)",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      transition: "width 0.3s ease, background 0.3s ease",
                    }}
                  />
                ))}
              </div>

              {/* Prev · counter · Next */}
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <button
                  onClick={prev}
                  aria-label="Previous scripture"
                  className="votw-nav-btn"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    border: "1px solid rgba(0,12,102,0.2)",
                    background: "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: PRIMARY,
                    transition: "background 0.2s, color 0.2s",
                  }}
                >
                  <ChevronLeft fontSize="small" />
                </button>

                <span
                  style={{
                    fontFamily: "Abel, sans-serif",
                    fontSize: 12,
                    color: "rgba(42,47,56,0.45)",
                    letterSpacing: "0.12em",
                    minWidth: 44,
                    textAlign: "center",
                  }}
                >
                  {pad(activeIdx + 1)} / {pad(SCRIPTURES.length)}
                </span>

                <button
                  onClick={next}
                  aria-label="Next scripture"
                  className="votw-nav-btn"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    border: "1px solid rgba(0,12,102,0.2)",
                    background: "transparent",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: PRIMARY,
                    transition: "background 0.2s, color 0.2s",
                  }}
                >
                  <ChevronRight fontSize="small" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
